import { PcConfiguration, ComponentOption } from "@/types/types";
import { mockComponents } from "@/data/mockPcComponents";
import { checkCompatibility } from "./componentCompatibility";

interface ConflictResolutionResult {
  resolvedConfiguration: Partial<PcConfiguration>;
  notifications: { title: string; description: string }[];
}

/**
 * Checks the current configuration for any components that have become incompatible
 * due to the most recently changed component, and finds replacements for them.
 * @param currentConfig The full PC configuration after a new component has been selected.
 * @param changedCategory The category of the component that was just changed (e.g., 'motherboard').
 * @returns An object with the components to be replaced and notifications for the user.
 */
export const resolveIncompatibilities = (
  currentConfig: PcConfiguration,
  changedCategory: string
): ConflictResolutionResult => {
  const resolvedConfiguration: Partial<PcConfiguration> = {};
  const notifications: { title: string; description: string }[] = [];

  // List of categories to check for conflicts. We can expand this as needed.
  // By using 'as const', we tell TypeScript these are literal string values, not general strings.
  const categoriesToCheck = ['cpu', 'motherboard', 'ram', 'case', 'cooler'] as const;

  for (const category of categoriesToCheck) {
    // Don't check the component that was just changed against itself.
    if (category === changedCategory) continue;

    const componentToCheck = currentConfig[category] as ComponentOption | undefined;
    if (!componentToCheck) continue; // Nothing selected in this category, so no conflict.

    // Get all possible options for the category we are checking.
    const allOptionsForCategory = mockComponents.find(c => c.id === category)?.options || [];
    if (allOptionsForCategory.length === 0) continue;

    // Get a list of components that are compatible with the *new* configuration.
    const compatibleOptions = checkCompatibility(currentConfig, category, allOptionsForCategory);

    // If the currently selected component is no longer in the list of compatible options, we have a conflict.
    const isStillCompatible = compatibleOptions.some(opt => opt.id === componentToCheck.id);

    if (!isStillCompatible) {
      // CONFLICT FOUND! Find the cheapest compatible replacement.
      const replacement = compatibleOptions.sort((a, b) => a.price - b.price)[0];

      if (replacement) {
        resolvedConfiguration[category] = replacement;
        notifications.push({
          title: `${componentToCheck.name} was replaced`,
          description: `It was incompatible with the new ${changedCategory}. Swapped to ${replacement.name}.`,
        });
      } else {
        // If no compatible replacement is found, we must remove the conflicting component.
        resolvedConfiguration[category] = null;
        notifications.push({
          title: `${componentToCheck.name} was removed`,
          description: `It was incompatible with the new ${changedCategory} and no replacement was found.`,
        });
      }
    }
  }

  return { resolvedConfiguration, notifications };
};
